class CartDiscount extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('.cart-drawer__discount-input');
    this.applyButton = this.querySelector('.cart-drawer__discount-apply');
    this.errorElement = this.querySelector('.cart-drawer__discount-error');

    this.applyButton.addEventListener('click', () => this.applyDiscount());
    // Enter inside #CartDrawer-Form would submit checkout — intercept it.
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.applyDiscount();
      }
    });
    this.querySelectorAll('.cart-drawer__discount-remove').forEach((button) =>
      button.addEventListener('click', (event) => {
        const code = event.currentTarget.closest('[data-discount-code]').dataset.discountCode;
        this.removeDiscount(code);
      })
    );
  }

  get appliedCodes() {
    return Array.from(this.querySelectorAll('[data-discount-code]')).map((el) => el.dataset.discountCode);
  }

  applyDiscount() {
    const code = this.input.value.trim();
    if (!code) return;
    const existing = this.appliedCodes;
    if (existing.some((c) => c.toUpperCase() === code.toUpperCase())) {
      this.showError(this.dataset.errorDuplicate);
      return;
    }
    this.hideError();
    this.setLoading(true);
    this.updateCartDiscounts(existing.concat(code), true)
      .then((cart) => {
        const entry = (cart.discount_codes || []).find((d) => d.code.toUpperCase() === code.toUpperCase());
        if (!entry || !entry.applicable) {
          // Roll back to the previous set (empty string clears all), no re-render needed.
          return this.updateCartDiscounts(existing, false).then(() => {
            this.showError(this.dataset.errorInvalid);
          });
        }
        return this.syncCheckoutDiscount(existing.concat(code)).then(() => this.renderCart(cart));
      })
      .catch(() => this.showError(this.dataset.errorGeneric))
      .finally(() => this.setLoading(false));
  }

  removeDiscount(code) {
    const remaining = this.appliedCodes.filter((c) => c.toUpperCase() !== code.toUpperCase());
    this.hideError();
    this.setLoading(true);
    this.updateCartDiscounts(remaining, true)
      .then((cart) => this.syncCheckoutDiscount(remaining).then(() => this.renderCart(cart)))
      .catch(() => this.showError(this.dataset.errorGeneric))
      .finally(() => this.setLoading(false));
  }

  // Registers the code server-side so it survives any checkout flow (including
  // Route's, which bypasses the cart form submit). /discount/ holds one code at
  // a time, so with stacked codes only the most recent gets this extra guarantee.
  syncCheckoutDiscount(codes) {
    if (codes.length > 0) {
      return fetch('/discount/' + encodeURIComponent(codes[codes.length - 1])).catch(() => {});
    }
    // No codes left — expire the cookie so a removed code can't resurface at checkout.
    document.cookie = 'discount_code=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    return Promise.resolve();
  }

  updateCartDiscounts(codes, withSections) {
    const body = { discount: codes.join(',') };
    if (withSections) {
      body.sections = this.getSectionsToRender().map((section) => section.section);
      body.sections_url = window.location.pathname;
    }
    return fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body: JSON.stringify(body) }).then((response) => {
      if (!response.ok) throw new Error(response.status);
      return response.json();
    });
  }

  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }

  renderCart(cart) {
    // Publish BEFORE swapping the DOM (same ordering as product-form.js) so
    // CartDrawerItems' onCartUpdate refetch lands on the soon-detached node.
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount', cartData: cart });
    this.getSectionsToRender().forEach((section) => {
      const element = document.getElementById(section.id);
      if (!element || !cart.sections || !cart.sections[section.section]) return;
      element.innerHTML = new DOMParser()
        .parseFromString(cart.sections[section.section], 'text/html')
        .querySelector(section.selector).innerHTML;
    });
  }

  setLoading(isLoading) {
    if (!this.isConnected) return; // element replaced by renderCart
    this.classList.toggle('cart-drawer__discount--loading', isLoading);
    this.applyButton.disabled = isLoading;
    this.input.disabled = isLoading;
    this.applyButton.setAttribute('aria-busy', isLoading);
  }

  showError(message) {
    if (!this.isConnected || !message) return;
    this.errorElement.textContent = message;
    this.errorElement.hidden = false;
  }

  hideError() {
    this.errorElement.hidden = true;
  }
}

if (!customElements.get('cart-discount')) customElements.define('cart-discount', CartDiscount);
